import React, { useCallback, useMemo } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ViewColumnsIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

import HeroImageUploader from "./shared/HeroImageUploader";

function Group({ title, subtitle, defaultOpen = true, children, t = (k, f) => f }) {
  return (
    <details open={defaultOpen} className="rounded-xl border border-gray-200 bg-white">
      <summary className="cursor-pointer select-none list-none px-4 py-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {subtitle ? <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div> : null}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">{t("navEditor.common.toggle", "Toggle")}</div>
      </summary>
      <div className="px-4 pb-4 pt-1">{children}</div>
    </details>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-1 rounded-xl bg-gray-100 border border-gray-200">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              active
                ? "bg-white text-gray-900 shadow-sm ring-1 ring-gray-200"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const LayoutAlignmentSelector = React.memo(function LayoutAlignmentSelector({ value, onChange, t }) {
  const ALIGN_OPTIONS = [
    {
      value: "left",
      label: t("navEditor.align.left", "Left"),
      Icon: ArrowLeftIcon,
      description: t("navEditor.align.leftDesc", "Left side"),
    },
    {
      value: "center",
      label: t("navEditor.align.center", "Center"),
      Icon: ViewColumnsIcon,
      description: t("navEditor.align.centerDesc", "Centered"),
    },
    {
      value: "right",
      label: t("navEditor.align.right", "Right"),
      Icon: ArrowRightIcon,
      description: t("navEditor.align.rightDesc", "Right side"),
    },
  ];

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <ViewColumnsIcon className="w-4 h-4" />
        {t("navEditor.layout.alignment", "Links Alignment")}
      </label>

      <div className="grid grid-cols-3 gap-2">
        {ALIGN_OPTIONS.map(({ value: v, label, Icon, description }) => {
          const active = value === v;
          return (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v)}
              className={`relative flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-all
                ${
                  active
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }
              `}
            >
              <div
                className={`mb-2 p-2.5 rounded-lg ${
                  active ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-600"
                }`}
              >
                <Icon className="w-5 h-5" />
              </div>

              <span className="text-xs font-medium text-gray-700 mb-0.5">{label}</span>
              <span className="text-xs text-gray-500">{description}</span>

              {active ? (
                <div className="absolute top-1.5 right-1.5">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                    <CheckIcon className="w-3 h-3 text-white" />
                  </div>
                </div>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="text-xs text-gray-500">
        {t("navEditor.layout.alignmentHint", "Controls where the links sit inside the navbar (desktop).")}
      </div>
    </div>
  );
});

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

export default function NavSectionEditor({ config = {}, onChange, uploadImage, t = (k, f) => f }) {
  const links = useMemo(() => safeArray(config.links), [config.links]);

  const direction = config.direction || "ltr";
  const alignment = config.alignment || (direction === "rtl" ? "right" : "left");

  // legacy support
  const showLogin = (config.showLogin ?? config.showManagerLogin) !== false;
  const loginButtonText =
    config.loginButtonText ?? config.managerButtonText ?? t("navEditor.login.defaultText", "Login");
  const loginTargetRoute = config.loginTargetRoute ?? config.managerTargetRoute ?? "/{slug}/manager";

  const setLinks = (next) => onChange?.("links", next);

  const addLink = () => {
    setLinks([...(links || []), { label: t("navEditor.links.newLinkLabel", "New Link"), href: "#contact" }]);
  };

  const updateLink = (idx, patch) => {
    const next = [...links];
    next[idx] = { ...(next[idx] || {}), ...(patch || {}) };
    setLinks(next);
  };

  const removeLink = (idx) => {
    const next = [...links];
    next.splice(idx, 1);
    setLinks(next);
  };

  const moveLink = (from, to) => {
    if (to < 0 || to >= links.length) return;
    const next = [...links];
    const [picked] = next.splice(from, 1);
    next.splice(to, 0, picked);
    setLinks(next);
  };

  const uploadAdapter = useCallback(
    async (file, kindString) => {
      if (!uploadImage) throw new Error("Upload disabled");
      const kind = String(kindString || "default");
      return uploadImage(file, { kind });
    },
    [uploadImage]
  );

  return (
    <div className="space-y-4">
      <Group
        t={t}
        title={t("navEditor.group.layout", "Layout")}
        subtitle={t("navEditor.group.layoutHelp", "Direction and desktop alignment")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">{t("navEditor.layout.direction", "Direction")}</div>

            <Segmented
              value={direction}
              onChange={(v) => onChange?.("direction", v)}
              options={[
                { value: "ltr", label: t("navEditor.layout.ltr", "LTR") },
                { value: "rtl", label: t("navEditor.layout.rtl", "RTL") },
              ]}
            />
          </div>

          <LayoutAlignmentSelector value={alignment} onChange={(v) => onChange?.("alignment", v)} t={t} />
        </div>
      </Group>

      <Group
        t={t}
        title={t("navEditor.group.brand", "Brand")}
        subtitle={t("navEditor.group.brandHelp", "Name, logo, and sticky behavior")}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-700 mb-1">{t("navEditor.brand.brandName", "Brand Name")}</div>
              <input
                value={config.brandName || ""}
                onChange={(e) => onChange?.("brandName", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t("navEditor.brand.brandNamePlaceholder", "My Business")}
              />
            </div>

            <div>
              <div className="text-sm text-gray-700 mb-2">{t("navEditor.brand.sticky", "Sticky")}</div>

              <Segmented
                value={config.sticky ? "yes" : "no"}
                onChange={(v) => onChange?.("sticky", v === "yes")}
                options={[
                  { value: "yes", label: t("navEditor.brand.stickyYes", "Sticky") },
                  { value: "no", label: t("navEditor.brand.stickyNo", "Normal") },
                ]}
              />

              <div className="text-xs text-gray-500 mt-2">{t("navEditor.brand.keepAtTop", "Keep navigation at top")}</div>
            </div>
          </div>

          <div className="space-y-3 pt-2 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-gray-700">{t("navEditor.brand.brandLogo", "Brand Logo")}</div>

              {config.brandLogo ? (
                <button
                  type="button"
                  onClick={() => onChange?.("brandLogo", "")}
                  className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-sm"
                >
                  {t("navEditor.common.remove", "Remove")}
                </button>
              ) : null}
            </div>

            <div className="grid md:grid-cols-2 gap-4 items-start">
              <div>
                <HeroImageUploader
                  kind="logo"
                  uploadImage={uploadAdapter}
                  onUploaded={(url) => onChange?.("brandLogo", url)}
                  disabled={!uploadImage}
                  t={t}
                />
              </div>

              <div className="flex items-center justify-center">
                {config.brandLogo ? (
                  <img
                    src={config.brandLogo}
                    alt={t("navEditor.brand.logoAlt", "Logo")}
                    className="w-20 h-20 rounded-2xl object-cover border"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl border border-dashed border-gray-300 bg-white flex items-center justify-center text-gray-400">
                    {t("navEditor.brand.noLogo", "No logo")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Group>

      <Group
        t={t}
        title={t("navEditor.group.links", "Links")}
        subtitle={t("navEditor.group.linksHelp", "Navbar navigation items")}
        defaultOpen
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-gray-900">{t("navEditor.links.title", "Links")}</div>
            <button
              type="button"
              onClick={addLink}
              className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm hover:bg-blue-100"
            >
              {t("navEditor.links.addLink", "Add Link")}
            </button>
          </div>

          {links.length === 0 ? <div className="text-sm text-gray-500">{t("navEditor.links.empty", "No links yet.")}</div> : null}

          <div className="space-y-4">
            {links.map((l, idx) => (
              <div key={idx} className="border rounded-xl p-4 bg-gray-50 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-medium text-gray-700">
                    {t("navEditor.links.link", "Link")} {idx + 1}
                    {l?.label ? <span className="text-gray-400 font-normal"> • {l.label}</span> : null}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => moveLink(idx, idx - 1)}
                      disabled={idx === 0}
                      className="px-2 py-1 text-xs rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveLink(idx, idx + 1)}
                      disabled={idx === links.length - 1}
                      className="px-2 py-1 text-xs rounded-lg border border-gray-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLink(idx)}
                      className="px-3 py-2 rounded-lg bg-red-50 text-red-700 text-sm hover:bg-red-100"
                    >
                      {t("navEditor.common.remove", "Remove")}
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">{t("navEditor.links.label", "Label")}</div>
                    <input
                      value={l?.label || ""}
                      onChange={(e) => updateLink(idx, { label: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder={t("navEditor.links.labelPlaceholder", "Home")}
                    />
                  </div>

                  <div>
                    <div className="text-xs text-gray-600 mb-1">{t("navEditor.links.href", "Href")}</div>
                    <input
                      value={l?.href || ""}
                      onChange={(e) => updateLink(idx, { href: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder={t("navEditor.links.hrefPlaceholder", "/{slug} or #contact or https://example.com")}
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      {t("navEditor.links.tipPrefix", "Tip: use")} <span className="font-mono">{"{slug}"}</span>{" "}
                      {t("navEditor.links.tipSuffix", "inside routes.")}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Group>

      <Group
        t={t}
        title={t("navEditor.group.login", "Login Button")}
        subtitle={t("navEditor.group.loginHelp", "Manager login button settings")}
        defaultOpen={false}
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm text-gray-700 mb-2">{t("navEditor.login.visibility", "Visibility")}</div>

            <Segmented
              value={showLogin ? "show" : "hide"}
              onChange={(v) => onChange?.("showLogin", v === "show")}
              options={[
                { value: "show", label: t("navEditor.login.show", "Show") },
                { value: "hide", label: t("navEditor.login.hide", "Hide") },
              ]}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-700 mb-1">{t("navEditor.login.buttonText", "Button Text")}</div>
              <input
                value={loginButtonText}
                onChange={(e) => onChange?.("loginButtonText", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t("navEditor.login.buttonTextPlaceholder", "Login")}
              />
            </div>

            <div>
              <div className="text-sm text-gray-700 mb-1">{t("navEditor.login.targetRoute", "Target Route")}</div>
              <input
                value={loginTargetRoute}
                onChange={(e) => onChange?.("loginTargetRoute", e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t("navEditor.login.targetRoutePlaceholder", "/{slug}/manager")}
              />
            </div>
          </div>

          <div className="text-xs text-gray-500">
            {t("navEditor.login.slugHintPrefix", "Use")} <span className="font-mono">{"{slug}"}</span>{" "}
            {t("navEditor.login.slugHintSuffix", "and it will be replaced automatically.")}
          </div>
        </div>
      </Group>
    </div>
  );
}